/**
 * Choco Select Component
 * A standardized select component that follows Choco's design system.
 * Built on top of HeroUI's Select with consistent styling and accessibility.
 */

import { Select, SelectItem, type SelectProps } from '@heroui/react';
import React from 'react';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface ChocoSelectBaseProps extends Omit<
  SelectProps,
  'children' | 'onSelectionChange' | 'selectionMode' | 'value'
> {
  /** Array of options to display */
  options: SelectOption[];
  /** Label for the select */
  label?: string;
  /** Description text that appears below the label */
  description?: string;
  /** Error message to display */
  errorMessage?: string;
  /** Whether the select is required */
  isRequired?: boolean;
  /** Whether to show descriptions for options */
  showDescriptions?: boolean;
  /** Custom class name for the select wrapper */
  className?: string;
}

interface ChocoSelectSingleProps extends ChocoSelectBaseProps {
  selectionMode?: 'single';
  /** Current selected value */
  value?: string;
  /** Callback when selection changes */
  onSelectionChange?: (value: string | null) => void;
}

interface ChocoSelectMultipleProps extends ChocoSelectBaseProps {
  selectionMode: 'multiple';
  /** Current selected values */
  value: string[];
  /** Callback when selection changes */
  onSelectionChange?: (values: string[]) => void;
}

export type ChocoSelectProps =
  | ChocoSelectSingleProps
  | ChocoSelectMultipleProps;

/**
 * A standardized select component that follows Choco's design system.
 *
 * @example
 * ```tsx
 * <ChocoSelect
 *   label="Chat Type"
 *   placeholder="Select chat type..."
 *   options={[
 *     { value: 'ordering', label: 'Ordering', description: 'Standard ordering chat' },
 *     { value: 'support', label: 'Support', description: 'Support chat' }
 *   ]}
 *   value={selectedValue}
 *   onSelectionChange={handleSelectionChange}
 *   isRequired
 * />
 * ```
 */
export const ChocoSelect: React.FC<ChocoSelectProps> = (componentProps) => {
  const {
    options,
    value,
    label,
    description,
    errorMessage,
    isRequired = false,
    showDescriptions = true,
    className = '',
    selectionMode,
    onSelectionChange,
    ...props
  } = componentProps;

  const handleSelectionChange = (keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') return;

    if (selectionMode === 'multiple') {
      onSelectionChange?.(Array.from(keys, (key) => String(key)));
      return;
    }

    const selectedKey = Array.from(keys)[0] as string;
    onSelectionChange?.(selectedKey || null);
  };

  let selectedKeys: string[] = [];

  if (selectionMode === 'multiple') {
    selectedKeys = value;
  } else if (value) {
    selectedKeys = [value];
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
          {isRequired && <span className="text-danger ml-1">*</span>}
        </label>
      )}

      {description && <p className="text-xs text-default-600">{description}</p>}

      <Select
        selectionMode={selectionMode}
        selectedKeys={selectedKeys}
        onSelectionChange={handleSelectionChange}
        errorMessage={errorMessage}
        isInvalid={!!errorMessage}
        variant="bordered"
        classNames={{
          trigger:
            'bg-default-100 border-default-300 hover:border-default-400 focus-within:border-primary shadow-sm hover:shadow-md transition-all duration-200',
          selectorIcon: 'right-3',
          label: 'text-default-700',
          value: 'text-foreground',
        }}
        {...props}
      >
        {options.map((option) => (
          <SelectItem
            key={option.value}
            textValue={option.label}
            isDisabled={option.disabled}
            className="text-left py-3 border-b border-divider last:border-b-0"
          >
            <div className="py-1">
              <div className="font-medium text-foreground">{option.label}</div>
              {showDescriptions && option.description && (
                <div className="text-xs text-default-600 mt-1">
                  {option.description}
                </div>
              )}
            </div>
          </SelectItem>
        ))}
      </Select>
    </div>
  );
};
